import { useState, type ChangeEvent } from 'react';
import {
  List, Button, Switch, Input, Select, Space, Typography, Slider, Popconfirm, message,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import type { GisLayer } from '@dtwin/shared';
import { DEFAULT_BASEMAP_URLS, generateId } from '@dtwin/shared';
import { AUTHORIZED_BASEMAPS, findAuthorizedBasemap } from '../../config/mapConfig';

const { Text } = Typography;

interface GisLayerPanelProps {
  layers: GisLayer[];
  onLayersChange: (layers: GisLayer[]) => void;
}

export default function GisLayerPanel({ layers, onLayersChange }: GisLayerPanelProps) {
  const handleAdd = () => {
    // 2026-09-01｜默认底图：高德卫星影像（稳定可用；天地图需先配置服务端许可+域名白名单）
    const defaultBasemap = AUTHORIZED_BASEMAPS.find((m) => m.key === 'amap_img') || AUTHORIZED_BASEMAPS[0];
    const newLayer: GisLayer = {
      id: generateId(),
      name: `${defaultBasemap.label} 底图`,
      type: 'basemap',
      url: defaultBasemap.url,
      visible: true,
      opacity: 1,
    };
    // 底图单选：添加新底图时移除其他底图（避免失败瓦片露出下层旧底图）
    onLayersChange([...layers.filter((l) => l.type !== 'basemap'), newLayer]);
  };

  const handleToggle = (id: string) => {
    onLayersChange(
      layers.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l)),
    );
  };

  const handleRemove = (id: string) => {
    onLayersChange(layers.filter((l) => l.id !== id));
  };

  const handleOpacityChange = (id: string, opacity: number) => {
    onLayersChange(
      layers.map((l) => (l.id === id ? { ...l, opacity } : l)),
    );
  };

  const handleTypeChange = (id: string, type: GisLayer['type']) => {
    onLayersChange(
      layers.map((l) =>
        l.id === id
          ? {
              ...l,
              type,
              url: type === 'basemap' ? (AUTHORIZED_BASEMAPS.find((m) => m.key === 'amap_img') || AUTHORIZED_BASEMAPS[0]).url : '',
            }
          : l,
      ),
    );
  };

  // 2026-09-07｜卫星影像标注：判断图层是否为卫星影像（天地图 img / 高德 style=6 影像）
  const isSatelliteBasemap = (layer: GisLayer): boolean =>
    layer.type === 'basemap' && (layer.url.includes('layer=img') || layer.url.includes('style=6'));

  // 是否已叠加注记层（存在 url 含 layer=cva 的 basemap）
  const hasAnnotation = (id: string): boolean => {
    const sat = layers.find((l) => l.id === id);
    if (!sat) return false;
    return layers.some((l) => l.type === 'basemap' && l.url.includes('layer=cva'));
  };

  // 标注开关：开 → 叠加 cva 注记层（天地图注记，道路名/地名）；关 → 移除 cva 注记层
  const handleToggleAnnotation = (id: string, on: boolean) => {
    if (on) {
      const cva = findAuthorizedBasemap('tianditu_cva');
      if (!cva || layers.some((l) => l.type === 'basemap' && l.url.includes('layer=cva'))) return;
      const anno: GisLayer = { id: generateId(), name: '天地图 注记', type: 'basemap', url: cva.url, visible: true, opacity: 1 };
      onLayersChange([...layers, anno]);
    } else {
      onLayersChange(layers.filter((l) => !(l.type === 'basemap' && l.url.includes('layer=cva'))));
    }
  };

  const handleUrlChange = (id: string, url: string) => {
    onLayersChange(layers.map((l) => (l.id === id ? { ...l, url } : l)));
  };

  return (
    <div style={{ padding: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <Text strong>GIS 图层</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAdd}>
          添加图层
        </Button>
      </div>

      <Select
        role="combobox"
        style={{ width: '100%', marginBottom: 12 }}
        placeholder="快速添加底图"
        allowClear
        onChange={(key: string) => {
          const authorized = findAuthorizedBasemap(key);
          const url = authorized?.url || DEFAULT_BASEMAP_URLS[key as keyof typeof DEFAULT_BASEMAP_URLS];
          if (!url) return;
          // 2026-09-07｜天地图街道叠加：选「天地图 街道地图」自动叠加「天地图 注记」层（道路名/地名标注）
          const layersToAdd: GisLayer[] = [];
          if (key === 'tianditu_vec') {
            const cva = findAuthorizedBasemap('tianditu_cva');
            if (cva) {
              layersToAdd.push({ id: generateId(), name: '天地图 注记', type: 'basemap', url: cva.url, visible: true, opacity: 1 });
            }
          }
          const base: GisLayer = {
            id: generateId(),
            name: `${authorized?.label ?? key} 底图`,
            type: 'basemap',
            url,
            visible: true,
            opacity: 1,
          };
          layersToAdd.unshift(base);
          // 底图单选：快速添加时移除其他底图
          onLayersChange([...layers.filter((l) => l.type !== 'basemap'), ...layersToAdd]);
        }}
        options={[
          ...AUTHORIZED_BASEMAPS.map((m) => ({ value: m.key, label: m.label })),
          { value: 'osm', label: 'OpenStreetMap (需VPN)' },
        ]}
      />

      <List
        dataSource={layers}
        locale={{ emptyText: '暂无图层' }}
        renderItem={(layer) => (
          <List.Item
            style={{ padding: '8px 0', flexDirection: 'column', alignItems: 'stretch' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Space>
                <Button
                  type="text"
                  size="small"
                  icon={layer.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  onClick={() => handleToggle(layer.id)}
                />
                <Text style={{ fontSize: 13 }}>{layer.name}</Text>
              </Space>
              <Popconfirm title="删除图层？" onConfirm={() => handleRemove(layer.id)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>

            <Space style={{ width: '100%', marginTop: 4 }} size="small">
              <Select
                role="combobox"
                size="small"
                value={layer.type}
                style={{ width: 100 }}
                onChange={(v) => handleTypeChange(layer.id, v)}
                options={[
                  { value: 'basemap', label: '底图' },
                ]}
              />
              {/* 2026-09-07｜卫星影像标注开关：激活则自动叠加天地图注记层（道路名/地名标注） */}
              {isSatelliteBasemap(layer) && (
                <Space size={4}>
                  <Text style={{ fontSize: 11, color: '#888' }}>标注</Text>
                  <Switch size="small" checked={hasAnnotation(layer.id)} onChange={(v) => handleToggleAnnotation(layer.id, v)} />
                </Space>
              )}
              <Slider
                style={{ flex: 1 }}
                min={0}
                max={1}
                step={0.1}
                value={layer.opacity}
                onChange={(v) => handleOpacityChange(layer.id, v)}
                tooltip={{ formatter: (v) => `${Math.round((v ?? 1) * 100)}%` }}
              />
            </Space>

            <Input
              size="small"
              style={{ marginTop: 4, fontSize: 12 }}
              value={layer.url}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleUrlChange(layer.id, e.target.value)}
              placeholder="图层 URL"
            />
          </List.Item>
        )}
      />
    </div>
  );
}
